import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Search,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  Palette,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import StatusPipeline from "@/components/StatusPipeline";
import MessageThread from "@/components/MessageThread";
import { api, formatApiError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TrackRequest = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(requestId || "");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showDeclineInput, setShowDeclineInput] = useState(false);

  const fetchRequest = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data } = await api.get(`/requests/track/${id.trim().toUpperCase()}`);
      setData(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (requestId) fetchRequest(requestId);
     
  }, [requestId]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error("Enter a Request ID");
      return;
    }
    navigate(`/track/${query.trim().toUpperCase()}`);
    fetchRequest(query);
  };

  const submitApproval = async (approved, note) => {
    setApproving(true);
    try {
      const { data: updated } = await api.post(`/requests/${data.request_id}/approval`, {
        approved,
        note: note || null,
      });
      setData(updated);
      toast.success(approved ? "Approval sent — thanks!" : "Response sent to the team");
      setShowDeclineInput(false);
      setDeclineReason("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-slate-100">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <button
          onClick={() => navigate("/")}
          data-testid="back-home-btn"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 text-sm mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Home
        </button>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
            / Live Tracking
          </div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">
            Track my <span className="gold-text">request</span>
          </h1>
          <p className="mt-3 text-slate-400">Enter your Request ID to see live progress.</p>
        </div>

        <form
          onSubmit={handleSearch}
          data-testid="track-form"
          className="flex flex-col sm:flex-row gap-3 bg-[#18181E] border border-white/10 rounded-2xl p-3 mb-10"
        >
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="track-id-input"
              value={query}
              onChange={(e) => setQuery(e.target.value.toUpperCase())}
              placeholder="GEC-XXXXXX"
              className="w-full bg-[#121216] border border-white/10 focus:border-amber-500 text-slate-100 placeholder:text-slate-600 rounded-xl pl-11 pr-4 py-3 font-mono-brand text-sm uppercase tracking-wider outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="track-search-btn"
            className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-extrabold uppercase tracking-wider px-6 py-3 rounded-xl shadow-lg shadow-amber-500/20 transition disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Track"}
          </button>
        </form>

        {error && (
          <div
            data-testid="track-error"
            className="flex items-start gap-3 bg-red-500/5 border border-red-500/30 text-red-300 rounded-2xl p-5 mb-8"
          >
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Request not found</div>
              <div className="text-sm text-red-300/70 mt-1">{error}</div>
            </div>
          </div>
        )}

        {data && (
          <div className="space-y-6" data-testid="track-result">
            {/* Header card */}
            <div className="bg-gradient-to-b from-[#18181E] to-[#121216] border border-white/10 rounded-2xl p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="font-mono-brand text-xs text-amber-400">/ Request ID</div>
                  <div className="font-display text-2xl sm:text-3xl font-extrabold uppercase mt-1">
                    {data.request_id}
                  </div>
                </div>
                <div className="font-display text-xs uppercase tracking-widest">
                  <span
                    className={`px-4 py-2 rounded-full border ${
                      data.status === "OPEN"
                        ? "bg-amber-500/10 border-amber-500/30 text-amber-400"
                        : data.status === "IN PROGRESS"
                        ? "bg-blue-500/10 border-blue-500/30 text-blue-400"
                        : data.status === "DELIVERY"
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                        : "bg-zinc-500/10 border-zinc-500/30 text-zinc-400"
                    }`}
                    data-testid="status-badge"
                  >
                    {data.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Pipeline */}
            <StatusPipeline current={data.status} />

            {/* Found vehicle photos + approval */}
            {data.found_vehicle_photos?.length > 0 && (
              <div
                className="bg-gradient-to-b from-[#18181E] to-[#121216] border border-amber-500/30 rounded-2xl p-6 sm:p-8 shadow-[0_0_60px_rgba(245,158,11,0.08)]"
                data-testid="found-vehicle-card"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
                      / Vehicle Preview
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight mt-2">
                      We found your <span className="gold-text">car</span>
                    </h3>
                    <p className="mt-2 text-sm text-slate-400 max-w-xl">
                      Take a look at the vehicle we sourced. Approve to move forward or let us
                      know what to change.
                    </p>
                  </div>
                  {data.customer_approval && (
                    <div
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest font-semibold border self-start ${
                        data.customer_approval.approved
                          ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                          : "bg-red-500/10 border-red-500/30 text-red-300"
                      }`}
                      data-testid="approval-badge"
                    >
                      {data.customer_approval.approved ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5" />
                      )}
                      {data.customer_approval.approved ? "Approved" : "Declined"}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  {data.found_vehicle_photos.map((p, i) => (
                    <a
                      key={p}
                      href={`${BACKEND_URL}/api/files/${p}`}
                      target="_blank"
                      rel="noreferrer"
                      className="group block aspect-[4/3] rounded-xl overflow-hidden border border-white/10 hover:border-amber-500/50 transition"
                      data-testid={`found-photo-${i}`}
                    >
                      <img
                        src={`${BACKEND_URL}/api/files/${p}`}
                        alt={`vehicle-${i}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </a>
                  ))}
                </div>

                {!data.customer_approval ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => submitApproval(true)}
                        disabled={approving}
                        data-testid="approve-vehicle-btn"
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-black font-extrabold uppercase tracking-wider px-6 py-3.5 rounded-xl shadow-lg shadow-emerald-500/20 transition active:scale-[0.98]"
                      >
                        <ThumbsUp className="w-4 h-4" /> Yes — I love it
                      </button>
                      <button
                        onClick={() => setShowDeclineInput((v) => !v)}
                        disabled={approving}
                        data-testid="decline-vehicle-btn"
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white font-semibold uppercase tracking-wider px-6 py-3.5 rounded-xl border border-white/15 transition"
                      >
                        <ThumbsDown className="w-4 h-4" /> No — keep looking
                      </button>
                    </div>
                    {showDeclineInput && (
                      <div className="bg-[#0A0A0C] border border-white/10 rounded-xl p-4 space-y-3">
                        <label className="text-xs uppercase tracking-widest font-semibold text-slate-400">
                          Tell us why (optional)
                        </label>
                        <textarea
                          value={declineReason}
                          onChange={(e) => setDeclineReason(e.target.value)}
                          data-testid="decline-reason-input"
                          placeholder="Color's wrong / mileage too high / want a different trim…"
                          className="w-full bg-[#121216] border border-white/10 focus:border-amber-500 text-slate-100 placeholder:text-slate-600 rounded-xl px-3 py-2.5 text-sm outline-none min-h-[80px]"
                        />
                        <button
                          onClick={() => submitApproval(false, declineReason)}
                          disabled={approving}
                          data-testid="decline-submit-btn"
                          className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold uppercase tracking-wider px-5 py-2.5 rounded-xl text-sm transition"
                        >
                          {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Feedback"}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-[#0A0A0C] border border-white/10 rounded-xl p-4 text-sm">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                      Your response · {new Date(data.customer_approval.at).toLocaleString()}
                    </div>
                    {data.customer_approval.note && (
                      <p className="text-slate-300 mt-2">{data.customer_approval.note}</p>
                    )}
                    <button
                      onClick={() => {
                        setShowDeclineInput(false);
                        setDeclineReason("");
                        submitApproval(!data.customer_approval.approved);
                      }}
                      disabled={approving}
                      data-testid="change-approval-btn"
                      className="mt-3 text-xs text-amber-400 hover:text-amber-300 underline"
                    >
                      Change my mind
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Messages */}
            <MessageThread requestId={data.request_id} customerName={data.customer_name} />

            {/* Closure reason */}
            {data.status === "CLOSED" && data.closure_reason && (
              <div
                data-testid="closure-reason"
                className="bg-zinc-500/5 border border-zinc-500/30 rounded-2xl p-6"
              >
                <div className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
                  Reason for closure
                </div>
                <p className="mt-2 text-slate-200">{data.closure_reason}</p>
              </div>
            )}

            {/* Vehicle details */}
            <div className="bg-[#18181E] border border-white/10 rounded-2xl p-6 sm:p-8">
              <h3 className="font-display text-lg font-bold uppercase tracking-wider text-amber-400 mb-5">
                Request Details
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <Field label="Make" value={data.make} />
                <Field label="Model" value={data.model} />
                <Field label="Year" value={data.year} icon={Calendar} />
                <Field
                  label="Max Mileage"
                  value={data.max_mileage ? `${data.max_mileage.toLocaleString()} mi` : "—"}
                />
                <Field label="Exterior" value={data.exterior_color || "—"} icon={Palette} />
                <Field label="Interior" value={data.interior_color || "—"} />
                <Field
                  label="Budget"
                  value={data.budget ? `$${Number(data.budget).toLocaleString()}` : "—"}
                  icon={DollarSign}
                />
                <Field label="Title" value={data.title_type || "Clean"} />
                <Field label="Delivery ZIP" value={data.delivery_zip} icon={MapPin} />
              </div>

              {data.desired_features && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
                    Desired Features
                  </div>
                  <p className="text-sm text-slate-300">{data.desired_features}</p>
                </div>
              )}
              {data.additional_notes && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
                    Additional Notes
                  </div>
                  <p className="text-sm text-slate-300">{data.additional_notes}</p>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-white/5 flex flex-wrap gap-4 text-sm text-slate-400">
                <div className="inline-flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-400" /> {data.customer_email}
                </div>
                {data.customer_phone && (
                  <div className="inline-flex items-center gap-2">
                    <Phone className="w-4 h-4 text-amber-400" /> {data.customer_phone}
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            {data.status_history?.length > 0 && (
              <div className="bg-[#18181E] border border-white/10 rounded-2xl p-6 sm:p-8">
                <h3 className="font-display text-lg font-bold uppercase tracking-wider text-amber-400 mb-5">
                  Timeline
                </h3>
                <ol className="relative border-l border-white/10 ml-2 space-y-5">
                  {data.status_history.map((h, i) => (
                    <li key={i} className="ml-5" data-testid={`timeline-${i}`}>
                      <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-amber-500 border-2 border-[#18181E]" />
                      <div className="font-mono-brand text-[10px] uppercase tracking-widest text-slate-500">
                        {new Date(h.at).toLocaleString()}
                      </div>
                      <div className="font-display text-sm font-bold uppercase tracking-wider text-white mt-0.5">
                        {h.status}
                      </div>
                      {h.note && <p className="text-sm text-slate-400 mt-1">{h.note}</p>}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, icon: Icon }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
      {label}
    </div>
    <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-slate-100">
      {Icon && <Icon className="w-3.5 h-3.5 text-amber-400" />}
      {value}
    </div>
  </div>
);

export default TrackRequest;
